/**
 * ImageBlock - Image element for DesignerCanvas
 *
 * Displays an image or a placeholder icon if no source is set.
 */

import { memo } from 'react';
import type { ImageBlockElement } from '@deep-agents/shared-types';

interface ImageBlockProps {
  element: ImageBlockElement;
  onUpdate?: (props: Partial<ImageBlockElement['props']>) => void;
}

export const ImageBlock = memo(({ element }: ImageBlockProps) => {
  const { src, alt = 'Image', objectFit = 'cover' } = element.props;

  // Map objectFit to CSS
  const objectFitClass = {
    contain: 'object-contain',
    cover: 'object-cover',
    fill: 'object-fill',
  }[objectFit];

  if (src) {
    return (
      <div className="w-full h-full overflow-hidden rounded">
        <img
          src={src}
          alt={alt}
          className={`w-full h-full ${objectFitClass}`}
          draggable={false}
        />
      </div>
    );
  }

  // Placeholder when no image is set
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-slate-50 rounded border-2 border-dashed border-slate-200 text-slate-400">
      {/* Image icon */}
      <svg
        className="w-8 h-8 mb-1"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
        />
      </svg>
      <span className="text-xs font-medium">Image</span>
    </div>
  );
});

ImageBlock.displayName = 'ImageBlock';
